import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { getProduct, fromWcProductDetail, getProductVariations } from '../../lib/wp-api';
import CustomerHeader from '../../components/ui/CustomerHeader';
import AuthenticationModal from '../../components/ui/AuthenticationModal';
import CartSidebar from '../../components/ui/CartSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProductImageGallery from './components/ProductImageGallery';
import ProductInfo from './components/ProductInfo';
import ProductTabs from './components/ProductTabs';
import RelatedProducts from './components/RelatedProducts';
import RecentlyViewed, { trackRecentlyViewed } from './components/RecentlyViewed';
import Footer from '../../components/ui/Footer';

const ProductDetails = () => {
  const navigate = useNavigate();
  const cart = useCart();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showCartSidebar, setShowCartSidebar] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(2);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  // Product data — id comes from URL (?id=N), product fetched from WC.
  // URL-param routing survives refresh and deep links cleanly; the previous
  // location.state-based approach lost data on reload.
  const [searchParams] = useSearchParams();
  const productId = searchParams.get('id');
  const [product, setProduct] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [variations, setVariations] = useState([]);
  const [selectedVariationImage, setSelectedVariationImage] = useState(null);

  const [related, setRelated] = useState([]);

  useEffect(() => {
    if (!productId) return;
    // Scroll to top when navigating between products (e.g. clicking a Recently
    // Viewed card from the bottom of the page) — otherwise the new product
    // loads above the fold and the shopper sees no visible change.
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    let cancelled = false;
    setProduct(null);
    setRelated([]);
    setLoadFailed(false);
    // Critical: reset variation-related state so the previous product's
    // selected variant image doesn't leak into the new product's gallery.
    setSelectedVariationImage(null);
    setVariations([]);
    // Per-stage error handling so a failure in transform / related-products
    // fetch doesn't show "Product couldn't load" when the actual product
    // fetched fine. loadFailed only flips true on the primary fetch failing.
    (async () => {
      let wc;
      try {
        wc = await getProduct(productId);
      } catch (e) {
        console.error('[product-details] primary fetch failed:', e);
        if (!cancelled) { setProduct(null); setLoadFailed(true); }
        return;
      }
      if (cancelled || !wc) {
        if (!cancelled && !wc) setLoadFailed(true);
        return;
      }
      // Transform — best-effort. If our sanitizer / attribute extractor throws
      // on an unusual product shape, fall back to a minimum viable product
      // object built from the raw API response so the page renders SOMETHING.
      let detail;
      try {
        detail = fromWcProductDetail(wc);
      } catch (e) {
        console.error('[product-details] transform failed, using minimal shape:', e);
        detail = {
          id: wc.id,
          name: wc.name || 'Product',
          images: Array.isArray(wc.images)
            ? wc.images.map((img, i) => ({ id: img?.id ?? i, url: img?.src ?? '', alt: img?.alt || wc.name || '' }))
            : [],
          price: parseFloat(wc.prices?.price || '0') / 100,
          description: wc.description || '',
          shortDescription: (wc.short_description || '').replace(/<[^>]+>/g, '').trim(),
          inStock: !!wc.is_in_stock,
          rating: parseFloat(wc.average_rating || '0') || 0,
          reviewCount: wc.review_count || 0,
          categories: wc.categories || [],
          attributes: [],
          variations: [],
          variants: [],
        };
      }
      if (!cancelled) setProduct(detail);
      // Related products — best-effort, swallow errors.
      try {
        const primaryCat = detail?.categories?.[0]?.slug;
        const { getProducts, fromWcProduct } = await import('../../lib/wp-api');
        const rows = await getProducts(
          primaryCat
            ? { per_page: 4, category: primaryCat, exclude: detail.id }
            : { per_page: 4, exclude: detail.id }
        );
        if (!cancelled) setRelated(rows.map(fromWcProduct));
      } catch (e) {
        console.error('[product-details] related fetch failed (ignored):', e);
      }
    })();
    return () => { cancelled = true; };
  }, [productId]);

  // Fetch full variation data (per-variation images, prices, stock) so the
  // ProductInfo picker can render image swatches for attribute terms that
  // are gibberish placeholders (Element1 etc.).
  useEffect(() => {
    let cancelled = false;
    if (!productId) return;
    getProductVariations(productId)
      .then((rows) => { if (!cancelled) setVariations(rows); })
      .catch(() => { if (!cancelled) setVariations([]); });
    return () => { cancelled = true; };
  }, [productId]);

  // Track the visited product for the Recently Viewed section.
  useEffect(() => {
    if (product?.id) trackRecentlyViewed(product);
  }, [product?.id]);



  useEffect(() => {
    // Check authentication status
    const authStatus = localStorage.getItem('isAuthenticated');
    setIsAuthenticated(authStatus === 'true');

    // Scroll to top on component mount
    window.scrollTo(0, 0);
  }, []);

  const handleAuthClick = () => {
    setShowAuthModal(true);
  };

  const handleAuthenticate = (authData) => {
    setIsAuthenticated(true);
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('userData', JSON.stringify(authData?.userData));
    showSuccessNotification('Successfully signed in!');
  };

  const handleCartClick = () => {
    setShowCartSidebar(true);
  };

  const handleAddToCart = (productData) => {
    const qty = Math.max(1, Number(productData?.quantity) || 1);
    cart.addItem(productData, qty);
    showSuccessNotification(`Added ${productData?.name} to cart!`);
  };

  // Wishlist toggle is handled inside ProductInfo via WishlistContext directly.
  const handleAddToWishlist = () => {};

  const handleRelatedProductAddToCart = (productData) => {
    cart.addItem(productData, 1);
    showSuccessNotification(`Added ${productData?.name} to cart!`);
  };

  // Add this block - handlers for cart operations
  const handleUpdateQuantity = (itemId, newQuantity) => {
    // Update cart item quantity logic
    console.log(`Updating item ${itemId} to quantity ${newQuantity}`);
  };

  const handleRemoveItem = (itemId) => {
    // Remove item from cart logic
    setCartItemCount((prev) => Math.max(0, prev - 1));
    showSuccessNotification('Item removed from cart');
  };
  // End of added block

  const showSuccessNotification = (message) => {
    setNotificationMessage(message);
    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(false);
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <CustomerHeader
        onCartClick={() => setShowCartSidebar(true)}
      />

      <div className="pt-20 pb-16 flex-1">
        {/* Breadcrumb */}
        <div className="bg-muted/30 border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav className="flex items-center space-x-2 text-sm">
              <Link to="/product-catalog" className="text-muted-foreground hover:text-foreground transition-colors">
                Products
              </Link>
              {(product?.categories || []).map((cat, idx, arr) => (
                <React.Fragment key={cat?.id ?? idx}>
                  <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                  {idx === arr.length - 1 ? (
                    <span className="text-foreground font-medium">{cat?.name}</span>
                  ) : (
                    <Link
                      to={`/product-catalog?category=${cat?.slug}`}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {cat?.name}
                    </Link>
                  )}
                </React.Fragment>
              ))}
              {(!product?.categories || product.categories.length === 0) && product?.name && (
                <>
                  <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                  <span className="text-foreground font-medium">{product.name}</span>
                </>
              )}
            </nav>
          </div>
        </div>

        {/* Product Details */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Product Images */}
            {loadFailed && !product && (
              <div className="col-span-2 p-12 text-center bg-card border border-border rounded-xl">
                <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">Product couldn't load</h2>
                <p className="text-muted-foreground mb-6">It may have been removed, or there was a temporary issue fetching the details.</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:opacity-90"
                >Try again</button>
              </div>
            )}
            <div className="lg:sticky lg:top-24 lg:self-start">
              {product && <ProductImageGallery
                images={product?.images}
                selectedImageUrl={selectedVariationImage}
                productName={product?.name} />}

            </div>

            {/* Product Information */}
            <div>
              {product && (
                <ProductInfo
                  product={product}
                  variations={variations}
                  onVariationImageChange={setSelectedVariationImage}
                  onAddToCart={handleAddToCart}
                  onAddToWishlist={handleAddToWishlist} />
              )}
            </div>
          </div>

          {/* Product Details Tabs */}
          <div className="mt-16">
            {product && <ProductTabs product={product} />}
          </div>

          {/* Related Products */}
          <div className="mt-16">
            <RelatedProducts products={related} onAddToCart={handleRelatedProductAddToCart} />
          </div>

          {/* Recently Viewed — client-side history, no backend */}
          <div className="mt-16">
            <RecentlyViewed currentProductId={product?.id} />
          </div>
        </div>

        {/* Back to Top Button */}
        <div className="fixed bottom-6 right-6 z-30">
          <Button
            variant="outline"
            size="icon"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="w-12 h-12 rounded-full shadow-elevation-2 bg-background/80 backdrop-blur-sm">

            <Icon name="ArrowUp" size={20} />
          </Button>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Success Notification */}
      {showNotification &&
      <div className="fixed top-20 right-4 z-50 bg-success text-success-foreground px-4 py-3 rounded-lg shadow-elevation-3 flex items-center space-x-2 animate-in slide-in-from-right">
          <Icon name="CheckCircle" size={20} />
          <span className="text-sm font-medium">{notificationMessage}</span>
        </div>
      }
      {/* Authentication Modal */}
      <AuthenticationModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthenticate={handleAuthenticate}
        initialMode="login" />

      {/* Cart Sidebar */}
      <CartSidebar
        isOpen={showCartSidebar}
        onClose={() => setShowCartSidebar(false)}
        onCheckout={() => navigate('/shopping-cart')}
        onUpdateQuantity={handleUpdateQuantity}
        onRemoveItem={handleRemoveItem} />

    </div>
  );
};

export default ProductDetails;